import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { WithholdingTaxFormFactory } from './withholding-tax-form.factory';
import { TranslateService } from '@ngx-translate/core';
import {OptionsMapper} from "@magieno/angular-advanced-forms";
import { CalculationsUtils } from '../../../../../utils/calculations.utils';
import { AlertMessageStore } from '../../../../../../core/stores/alert-message.store';
import {
  CalculationKeynameEnum,
} from '../../../../../../../../../../../common/src/enums/calculations/calculation-keyname.enum';
import {
  WithholdingTaxOptions,
} from '../../../../../../../../../../../common/src/options/calculations/withholding-tax/withholding-tax.options';
import { QuickCalculationBaseForm } from '../../../base-form/form/base-form/quick-calculation-base.form';
import { CalculationsApiController } from '../../../../../api-controllers/calculations.api-controller';
import { Subscription } from 'rxjs';
import { QuickCalculationManager } from '../../../../../managers/quick-calculation.manager';
import { v4 as uuid } from 'uuid';
import { QuickCalculationCacheModel } from '../../../../../models/quick-calculation-cache.model';
import {
  WithholdingTaxResult,
} from '../../../../../../../../../../../common/src/results/calculations/withholding-tax/withholding-tax.result';
import {
  CalculationRunnerResultsInterface,
} from '../../../../../../../../../../../common/src/interfaces/calculation-runner-results.interface';
import { FormLifecycleStatusEnum } from '@magieno/angular-advanced-forms';

@Component({
  selector: 'app-quick-salary-contributions-form',
  templateUrl: './withholding-tax-form.component.html',
  styleUrls: ['./withholding-tax-form.component.scss'],
  standalone: false,
})
export class WithholdingTaxFormComponent extends QuickCalculationBaseForm<WithholdingTaxOptions> implements OnInit, OnDestroy {

  isExecuteButtonEnabled: boolean = false;

  result?: CalculationRunnerResultsInterface<WithholdingTaxResult>;

  buttonClickSubscription: Subscription;

  id: string;

  @Input()
  quickCalculationCache?: QuickCalculationCacheModel = undefined;

  constructor(formFactory: WithholdingTaxFormFactory,
              translateService: TranslateService,
              optionsMapper: OptionsMapper,
              calculationsUtils: CalculationsUtils,
              alertMessageStore: AlertMessageStore,
              private readonly calculationsApiController: CalculationsApiController,
              private readonly quickCalculationManager: QuickCalculationManager,
  ) {
    super(
      formFactory,
      translateService,
      optionsMapper,
      calculationsUtils,
      alertMessageStore,
      CalculationKeynameEnum.WithholdingTax,
    );
    this.optionsType = WithholdingTaxOptions;
  }

  _lifecycleStatus: FormLifecycleStatusEnum = FormLifecycleStatusEnum.ReadyForInput;

  get lifecycleStatus(): FormLifecycleStatusEnum {
    return this._lifecycleStatus;
  }

  set lifecycleStatus(value: FormLifecycleStatusEnum) {
    this._lifecycleStatus = value;

    this.form.lifecycleStatus = value;
  }

  saveCache() {
    let locale = 'en-CA';

    if (this.translateService.currentLang.startsWith('fr')) {
      locale = 'fr-CA';
    }

    this.quickCalculationManager.save(CalculationKeynameEnum.WithholdingTax, `Salaire: ${new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CAD',
    }).format(this.form.value.employmentIncome / 100)}`, this.form.value, this.result, this.id);
  }

  override ngOnInit() {
    super.ngOnInit();

    if (this.quickCalculationCache) {
      this.id = this.quickCalculationCache.id;
      this.form.setValue(this.quickCalculationCache.options);
      this.result = this.quickCalculationCache.results;
    } else {
      this.id = uuid();
      const options = new WithholdingTaxOptions();
      options.shouldQPPBeCalculated = true;
      options.shouldQPIPBeCalculated = true;
      options.shouldEIBeCalculated = true;
      options.shouldCNTBeCalculated = true;
      options.shouldCNESSTBeCalculated = false;
      options.isEmployeeSubjectToEI = true;

      this.form.setValue(options);
    }

    this.form.value$.subscribe(async value => {
      this.saveCache();
    });

    this.buttonClickSubscription = this.form.buttonClick$.subscribe(value => {
      this.calculate();
    });
  }

  async calculate() {
    this.lifecycleStatus = FormLifecycleStatusEnum.Sending;

    const response = await this.calculationsApiController.instantRun(this.form.value);

    this.lifecycleStatus = FormLifecycleStatusEnum.ReadyForInput;

    this.result = response;

    this.saveCache();
  }

  employeeContributionsSubtotal(): number {
    return (this.result.results.employeeContributions.EI ?? 0) +
      (this.result.results.employeeContributions.QPP ?? 0) +
      (this.result.results.employeeContributions.QPIP ?? 0);
  }

  employeeContributionsTotal(): number {
    return this.employeeContributionsSubtotal();
  }

  employerContributionsSubtotal(): number {
    return (this.result.results.employerContributions.EI ?? 0) +
      (this.result.results.employerContributions.QPP ?? 0) +
      (this.result.results.employerContributions.QPIP ?? 0) +
      (this.result.results.employerContributions.FSS ?? 0) +
      (this.result.results.employerContributions.CNESST ?? 0);
  }

  employerContributionsTotal(): number {
    return this.employerContributionsSubtotal() + (this.result.results.employerContributions.CNT ?? 0);
  }
}
